import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Modal, Button, Dropdown, Tooltip } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  SettingOutlined
} from '@ant-design/icons';
import ErpDataGridToolbar from './ErpDataGridToolbar';
import ErpDataGridFormModal from './ErpDataGridFormModal';
import ErpDataGridFilterChips from './ErpDataGridFilterChips';
import ErpDataGridBulkBar from './ErpDataGridBulkBar';
import ErpDataGridPagination from './ErpDataGridPagination';
import ErpDataGridDetailDrawer from './ErpDataGridDetailDrawer';
import {
  ErpDataGridSkeleton,
  ErpDataGridEmpty,
  ErpDataGridError
} from './ErpDataGridStates';
import useErpGridPersistence from './useErpGridPersistence';
import {
  fromAntdTableChange,
  applyColumnOrder,
  applyClientSort,
  evaluateAdvancedFilters,
  findColumnAndSiblings,
  flattenColumnKeys,
  groupRowsByField,
  buildGroupedRowsFromServerMeta,
  aggregateRowsForColumn,
  isGroupHeaderRow,
  countAdvancedFilterRules
} from './gridQuery';
import ErpGridEditableCell from './ErpGridEditableCell';
import ErpGridAdvancedFilterModal from './ErpGridAdvancedFilterModal';
import ErpGridSummaryFooter from './ErpGridSummaryFooter';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Reusable ERP Data Grid (Stage 6A foundation)
//
// Enterprise-grade AntD Table wrapper for ERP master-data and
// transactional surfaces: Customer / Product / Process / Other
// Charges / Work Orders / Dispatch / Production / Sales Invoice / Reports.
//
// Surfaces supported (each can be opted in via props):
//   - toolbar (search + columns + refresh + clear + export + add)
//   - active-filter chip strip
//   - row selection + bulk-action bar with Delete confirmation
//   - hover-reveal action column (⋯ default; icons on row hover)
//   - row-click slide-over detail drawer (read-only)
//   - sticky pagination footer with Indian numbering
//   - loading skeleton / calm empty / soft-tinted error states
//   - uncontrolled Add/Edit/View form modal (renderForm + onSubmit)
//
// Two usage patterns supported:
//   1. Uncontrolled CRUD — pass `renderForm` and `onSubmit`; the
//      grid manages the Add/Edit/View modal lifecycle itself.
//   2. Controlled CRUD  — pass `onAdd` / `onEdit` / `onView` /
//      `onDelete` and run your own modals/drawers in the parent.
//
// Visual constants (per the Stage 6A design tokens):
//   header bg #F8FAFC · row hover rgba(59,130,246,0.04) ·
//   38 px row · 13 px body · 11 px secondary · primary accent #2563EB.
// ─────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_PAGE_SIZE_OPTIONS = ['15', '25', '50', '100'];

// Density → AntD size + scoped class.
const ROW_DENSITY = {
  compact:     { size: 'small',   className: 'erp-grid--compact' },
  comfortable: { size: 'middle',  className: 'erp-grid--comfortable' },
  spacious:    { size: 'large',   className: 'erp-grid--spacious' }
};

// Muted em-dash used for the global empty-cell fallback.
const MUTED_DASH = <span className="erp-grid__muted">—</span>;

// Walk a (possibly nested) column tree and collect every leaf column.
function flattenColumns(columns) {
  const out = [];
  for (const col of columns || []) {
    if (col && Array.isArray(col.children) && col.children.length) {
      out.push(...flattenColumns(col.children));
    } else if (col && (col.dataIndex !== undefined || col.key !== undefined)) {
      out.push(col);
    }
  }
  return out;
}

// Read a possibly-nested dataIndex (string | string[]) off a record.
function readField(record, dataIndex) {
  if (!record || dataIndex === undefined || dataIndex === null) return undefined;
  if (Array.isArray(dataIndex)) {
    let cur = record;
    for (const k of dataIndex) {
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  }
  return record[dataIndex];
}

// Resolve permission/visibility → boolean for a record.
function resolveAction(value, record) {
  if (value === undefined || value === null) return true;
  if (typeof value === 'function') return Boolean(value(record));
  return Boolean(value);
}

// Walk the (possibly nested) column tree and prune cells whose key is
// in `hiddenKeys`. Empty parent groups are dropped automatically.
function pruneColumns(columns, hiddenKeys) {
  if (!hiddenKeys || hiddenKeys.size === 0) return columns;
  const walk = (cols) => {
    const out = [];
    for (const col of cols || []) {
      if (col && Array.isArray(col.children) && col.children.length) {
        const kids = walk(col.children);
        if (kids.length) out.push({ ...col, children: kids });
      } else {
        const key = col.key || col.dataIndex;
        if (!hiddenKeys.has(key)) out.push(col);
      }
    }
    return out;
  };
  return walk(columns);
}

// Cell wrapper that clips overflowing text with an ellipsis and reveals
// the full value in an AntD Tooltip — but only when truncation actually
// happens. We measure on mouse enter (scrollWidth vs clientWidth on
// the inner span) so non-overflowing cells stay silent.
function ErpOverflowCell({ title, children }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const handleEnter = () => {
    const el = ref.current;
    if (el && el.scrollWidth > el.clientWidth + 1) setOpen(true);
  };
  const handleLeave = () => setOpen(false);

  return (
    <Tooltip
      title={title}
      open={open}
      placement="topLeft"
      mouseEnterDelay={0.15}
      destroyTooltipOnHide
    >
      <span
        ref={ref}
        className="erp-grid__overflow"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {children}
      </span>
    </Tooltip>
  );
}

// Wrap leaf columns marked `ellipsis: true` so overflowing text shows
// an AntD Tooltip on hover (only when the cell actually clips). AntD's
// native title tooltip is disabled via `ellipsis: { showTitle: false }`
// so we don't end up with two stacked tooltips.
//
// Per-column opt-out: `{ ellipsisTooltip: false }`.
// Custom tooltip text:  `{ tooltipText: (value, record, index) => string }`.
function withOverflowTooltip(columns) {
  const walk = (cols) =>
    (cols || []).map((col) => {
      if (!col) return col;
      if (Array.isArray(col.children) && col.children.length) {
        return { ...col, children: walk(col.children) };
      }
      if (!col.ellipsis) return col;
      if (col.ellipsisTooltip === false) return col;
      const userRender = col.render;
      return {
        ...col,
        // Keep cell-level ellipsis CSS but suppress the native browser
        // `title` tooltip so it doesn't double up with the AntD Tooltip.
        ellipsis:
          typeof col.ellipsis === 'object'
            ? { ...col.ellipsis, showTitle: false }
            : { showTitle: false },
        render: (value, record, index) => {
          const inner = userRender
            ? userRender(value, record, index)
            : value;
          // Empty cells flow through to withEmptyFallback unchanged.
          if (inner === null || inner === undefined || inner === '') {
            return inner;
          }
          let tipText;
          if (typeof col.tooltipText === 'function') {
            tipText = col.tooltipText(value, record, index);
          } else if (value !== null && value !== undefined && value !== '') {
            tipText = String(value);
          } else if (typeof inner === 'string') {
            tipText = inner;
          }
          if (!tipText) return inner;
          return <ErpOverflowCell title={tipText}>{inner}</ErpOverflowCell>;
        }
      };
    });
  return walk(columns);
}

// ─── Group header rendering (Stage 6A Increment 2) ───────────────
//
// When a row is a group-header pseudo-row (record.__erpGroupHeader),
// we collapse all data-column cells into ONE wide cell that carries
// the group label, member count, and per-column aggregates. The
// action column (if any) stays separate to keep AntD's fixed-right
// layout happy.

function ErpGridGroupHeaderCell({ record /* aggregateColumns unused — see below */ }) {
  const label =
    record.__erpGroupLabel === null || record.__erpGroupLabel === undefined
      ? '(none)'
      : String(record.__erpGroupLabel);
  const count = record.__erpGroupCount || 0;
  // Stage 6A.2K-fix — group header is now a minimal "Label (N)" strip.
  // The record-count pill and per-column aggregate chips were dropped
  // per spec: callers want a compact band that just communicates the
  // group identity and member count. Per-row footer aggregates still
  // live in the summary row at the bottom of the page.
  return (
    <div className="erp-grid__group-header">
      <span className="erp-grid__group-header-label">{label}</span>
      <span className="erp-grid__group-header-count">
        ({count.toLocaleString('en-IN')})
      </span>
    </div>
  );
}

// Wrap each column so group-header pseudo-rows render as a single
// wide cell. The first non-action leaf gets `colSpan = N` (where N is
// the count of non-action leaves); all other non-action leaves get
// `colSpan: 0`, which AntD removes from the layout. The action column
// is left alone so its fixed-right pinning remains intact.
function withGroupHeaderRendering(columns, aggregateColumns) {
  const allLeaves = flattenColumns(columns);
  const dataLeaves = allLeaves.filter((c) => c.key !== '__erp_actions__');
  const spanCount = dataLeaves.length;
  if (spanCount === 0) return columns;
  let firstSeen = false;
  const walk = (cols) =>
    (cols || []).map((col) => {
      if (!col) return col;
      if (Array.isArray(col.children) && col.children.length) {
        return { ...col, children: walk(col.children) };
      }
      const isAction = col.key === '__erp_actions__';
      const isFirstDataLeaf = !isAction && !firstSeen;
      if (!isAction) firstSeen = true;
      const userOnCell = col.onCell;
      const userRender = col.render;
      return {
        ...col,
        onCell: (record, rowIndex) => {
          const base = typeof userOnCell === 'function' ? userOnCell(record, rowIndex) : null;
          if (record && record.__erpGroupHeader && !isAction) {
            return {
              ...(base || {}),
              colSpan: isFirstDataLeaf ? spanCount : 0,
              className: `${(base && base.className) || ''} erp-grid__group-header-cell`.trim()
            };
          }
          return base || {};
        },
        render: (value, record, index) => {
          if (record && record.__erpGroupHeader) {
            if (isAction) return null;
            if (!isFirstDataLeaf) return null;
            return (
              <ErpGridGroupHeaderCell
                record={record}
                aggregateColumns={aggregateColumns}
              />
            );
          }
          return userRender ? userRender(value, record, index) : value;
        }
      };
    });
  return walk(columns);
}

// Wrap every leaf column's `render` so a nullish/empty result becomes
// the muted em-dash. This is the global "no blank cells" guarantee
// described in the Stage 6A spec — opt out per-column with
// `{ emptyFallback: false }` on the column definition, or globally
// via the grid prop `emptyFallback={false}`.
function withEmptyFallback(columns) {
  const walk = (cols) =>
    (cols || []).map((col) => {
      if (!col) return col;
      if (Array.isArray(col.children) && col.children.length) {
        return { ...col, children: walk(col.children) };
      }
      if (col.emptyFallback === false) return col;
      const userRender = col.render;
      return {
        ...col,
        render: (value, record, index) => {
          if (userRender) {
            const out = userRender(value, record, index);
            if (out === null || out === undefined || out === '') return MUTED_DASH;
            return out;
          }
          if (value === null || value === undefined || value === '') return MUTED_DASH;
          return value;
        }
      };
    });
  return walk(columns);
}

// CSV cell-quoting (RFC 4180): wrap if the value contains a comma,
// double-quote, or newline; double any embedded double-quote.
function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Mac vs everything else — controls the search shortcut hint label.
function detectMac() {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  return ua.includes('mac') || ua.includes('iphone') || ua.includes('ipad');
}

// True if a click on a row should be IGNORED for row-click purposes
// (the click was inside the row's checkbox column or action cell). We
// use a closest-ancestor walk rather than DOM equality so taps on
// nested children (e.g. the Button icon span) are also filtered.
function isInteractiveRowClick(e) {
  const t = e && (e.target || e.srcElement);
  if (!t || typeof t.closest !== 'function') return false;
  return Boolean(
    t.closest('.ant-table-selection-column') ||
      t.closest('.erp-grid__actions') ||
      t.closest('.ant-checkbox-wrapper') ||
      t.closest('button') ||
      t.closest('a') ||
      t.closest('.ant-table-filter-trigger') ||
      t.closest('.ant-dropdown-menu')
  );
}

export default function ErpDataGrid({
  // ── identity / data
  title,
  description,
  columns,
  dataSource,
  rowKey = 'id',
  loading = false,
  error = null,

  // ── persistence (Stage 6A foundation)
  // `gridId` enables localStorage persistence under
  // `glassi:grid:{tenantId}:{userId}:{gridId}`. Only UI prefs are
  // stored — never row data, never tokens. See useErpGridPersistence
  // for the exact key shape + safety rules.
  gridId,

  // ── server-mode wiring (Stage 6A foundation)
  // When `serverMode` is true the grid trusts `dataSource` as the
  // current page only and emits page/sort/filter changes via
  // `onQueryChange(patch)` instead of mutating in-memory state.
  // `total` drives the pagination footer. Compatible with the
  // `useErpMasterQuery` hook — pass its `gridHandlers` straight in.
  serverMode = false,
  total: totalProp,
  query: queryProp,
  onQueryChange,
  // Server-supplied summary map (Stage 6A Increment 1). Keyed by
  // column key or dataIndex. Drives the optional footer row when
  // a column declares `aggregate` OR the server pushes pre-computed
  // totals. Client-mode grids can omit this and rely on the local
  // per-page aggregator inside ErpGridSummaryFooter.
  summary,

  // Server-supplied per-group metadata (Stage 6A Increment 3). When
  // present and `groupBy` is active, the grid renders group headers
  // using `meta.groups[*].label / count / summary` instead of
  // computing them client-side from the current page. Shape:
  //
  //   meta = { grouped: true, groupBy: 'state', groups: [
  //     { key, label, count, summary: { credit_limit: 5000000, … } },
  //     …
  //   ] }
  //
  // Absent meta → grid falls back to the existing client-side path
  // (see groupRowsByField), so callers can opt in incrementally.
  meta,

  // ── master-detail / expandable rows (Stage 6A foundation)
  // Passthrough to AntD `<Table expandable={...} />`. When the
  // detail drawer is also configured, the user can still trigger it
  // via the row-double-click gesture — the two surfaces don't
  // conflict because expand uses the chevron column.
  expandable,

  // ── inline editing (Stage 6A Increment 1)
  // When `onUpdateCell` is provided, columns marked
  // `editable: true` switch to ErpGridEditableCell wrappers. The
  // grid calls `onUpdateCell(record, field, newValue, original)`
  // and expects a Promise. Resolve to commit, reject to revert.
  // Without this prop, all cells stay read-only — even
  // `editable: true` columns.
  onUpdateCell,
  // Optional permission helper. `(record, column) => boolean`. When
  // it returns false for a row/column pair, that specific cell is
  // not editable even if `editable: true`. Lets RBAC decisions live
  // outside the grid.
  canEditCell,

  // ── multi-column sort (Stage 6A Increment 1)
  // AntD 5 supports multi-sort when EVERY sortable column declares
  // a numeric `sorter.multiple` priority. When `multiSort` is on,
  // we auto-assign these. Shift+click on additional sortable
  // headers extends the active sort array; a normal click resets it.
  multiSort = true,

  // ── grouping (Stage 6A Increment 2)
  // `groupBy` is a column dataIndex/key. When set, client-mode grids
  // insert header rows between groups (see groupRowsByField). Server-
  // mode forwards the value through `query.groupBy` and expects the
  // backend to return grouped rows or grouped metadata.
  // Mark columns `groupable: true` to surface them in the Group-by
  // dropdown. When `groupBy` is null/empty, grouping is off.
  groupBy: groupByProp,

  // ── virtualization (Stage 6A Increment 2)
  // AntD 5.6+ supports a `virtual` Table prop for large in-memory
  // row counts. It is INCOMPATIBLE with expandable rows and AntD's
  // own Table.Summary footer in some setups, so we keep it opt-in
  // and auto-disable when those features are also active. Default
  // is `false` — server-side pagination remains the canonical path
  // for database-scale data. See the developer guide §15.
  virtualized = false,

  // ── density / layout
  rowHeight = 'compact',
  bordered = true,
  stickyHeader = true,
  stickyOffset = 64,
  stickyToolbar = true, // toolbar pins below the app topbar on scroll
  scroll,
  rowClassName,
  size, // override resolved density `size`
  emptyFallback = true,

  // ── selection
  selectable = false,
  rowSelection,
  bulkActions,

  // ── toolbar
  toolbar = true,
  searchable = true,
  searchPlaceholder,
  searchableFields,
  searchShortcut = true,
  exportable = false,
  exportFilename,
  toolbarExtra = null,

  // ── pagination
  pageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  pagination = true,

  // ── CRUD
  actions = {},
  permissions = {},
  // Default: action column flows at the natural end of the table so
  // it never overlays a data column under horizontal scroll. Opt in to
  // `stickyActions` only when the master has many columns AND the
  // action affordance must stay reachable mid-scroll.
  stickyActions = false,
  // Matches the checkbox column width (rowSelection.columnWidth: 44)
  // so the left and right pinned utility columns have visual rhyme.
  actionWidth = 44,
  // Header label defaults to a centered gear icon (set later when
  // `actionsLabel` is undefined). Modules can override with a string
  // or any JSX. Pass an empty string to render a blank header cell.
  actionsLabel,
  onAdd,
  onEdit,
  onView,
  onDelete,
  onBulkDelete,
  addButtonText = 'Add',

  // ── built-in modal (optional uncontrolled CRUD)
  renderForm,
  formInitialValues,
  formTitles,
  formModalWidth = 760,
  onSubmit,

  // ── detail drawer (row click)
  onRowClick,            // (record) => void — if provided, overrides built-in drawer
  detailGroups,          // when truthy, row click opens built-in read-only Drawer
  detailDrawerTitle,     // string | (record) => string
  detailDrawerSubtitle,  // string | (record) => string
  detailDrawerWidth = 460,

  // ── right-click context menu (Stage 6A.2G) ─────────────────────
  // Optional. When provided, right-click on a data row opens an AntD
  // Dropdown menu at the mouse position. Each action is shaped as:
  //   {
  //     key: 'view',
  //     label: 'View',
  //     icon: <EyeOutlined />,        // optional
  //     danger: false,                // optional — red styling
  //     disabled: (record) => false,  // optional — hides item
  //     visible:  (record) => true,   // optional — hides item entirely
  //     onClick:  (record) => void    // required
  //   }
  // The grid never reads `permission` itself — caller is expected to
  // filter the array (or use `visible`) before passing it in. Context
  // menu is OFF until you pass this prop; existing grids stay unchanged.
  contextMenuActions,

  // ── misc
  onRefresh,
  emptyTitle,
  emptyText = 'No records to display.',
  emptyCtaLabel,
  className = '',
  tableClassName = '',

  // ── theme (Stage 6A.2J) ─────────────────────────────────────────
  // Visual theme applied to the grid via a wrapper CSS class. The
  // grid stays functionally identical across themes; only the
  // palette + spacing tokens change.
  //
  //   'light' (default) — Glass-i light spreadsheet theme. Maps to
  //                       `.erp-grid--theme-light` in index.css.
  //                       Tokenised CSS variables (--erp-grid-*) drive
  //                       header/body/hover/selected/fixed-shadow.
  //
  // Future themes (compact, high-density, dark) plug in here without
  // touching grid internals — just add an entry to the index.css
  // theme block and pass theme="compact" from the caller.
  theme = 'light'
}) {
  // ── persistence (Stage 6A) ────────────────────────────────────
  // When `gridId` is set, hydrate hidden columns + page size from
  // localStorage and persist any user change back. The hook returns
  // a stable `setPersisted({ patch })` that we call as the user
  // interacts; lookups are O(1) because the slot is keyed on
  // {tenantId, userId, gridId}.
  const persistence = useErpGridPersistence({
    gridId,
    defaults: { hiddenColumnKeys: [], pageSize }
  });
  const persistedState = persistence.state || {};

  // ── internal state ────────────────────────────────────────────
  const [searchText, setSearchText] = useState(
    () => (gridId ? persistedState.search || '' : '')
  );

  // Stage 6A.2K-fix — server-mode search-to-query bridge.
  // Local `searchText` drove client-mode filtering only. In serverMode
  // the parent's query state owns search, so when the operator types
  // (or clears) the toolbar search box we forward the value to the
  // adapter via `onQueryChange`. Debounce mirrors the typing burst on
  // the data layer (useErpMasterQuery already debounces its fetch on
  // `search`, but a small extra delay here also avoids dispatching a
  // query change on every keystroke). Page resets to 1 so the new
  // result set starts from the top.
  useEffect(() => {
    if (!serverMode || typeof onQueryChange !== 'function') return undefined;
    if (queryProp && (queryProp.search || '') === (searchText || '')) {
      return undefined;
    }
    const handle = setTimeout(() => {
      onQueryChange({ search: searchText || '', page: 1 });
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText, serverMode, onQueryChange, queryProp]);
  const [tableState, setTableState] = useState(() => ({
    pagination: {
      current: 1,
      pageSize: gridId
        ? persistedState.pageSize || pageSize
        : pageSize
    },
    filters: gridId ? persistedState.filters || {} : {},
    sorter: gridId ? persistedState.sorter || {} : {}
  }));
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState(
    () =>
      new Set(
        gridId && Array.isArray(persistedState.hiddenColumnKeys)
          ? persistedState.hiddenColumnKeys
          : []
      )
  );
  // Stage 6A Increment 1 — column reorder, width persistence, and
  // the advanced-filter modal-open flag. None of these alter behavior
  // until the user interacts; defaults preserve the source order.
  const [columnOrder, setColumnOrder] = useState(
    () =>
      gridId && Array.isArray(persistedState.columnOrder)
        ? persistedState.columnOrder
        : []
  );
  const [columnWidths, setColumnWidths] = useState(
    () =>
      gridId &&
      persistedState.columnWidths &&
      typeof persistedState.columnWidths === 'object'
        ? persistedState.columnWidths
        : {}
  );
  // Grouping state — controlled (server-mode passes `groupBy` via the
  // query) or uncontrolled (client-mode owns it locally + persists).
  // We always keep a local mirror so the toolbar dropdown can reflect
  // the current selection without re-rendering the whole grid.
  const [groupBy, setGroupBy] = useState(() => {
    if (serverMode) return groupByProp || queryProp?.groupBy || null;
    if (gridId && persistedState.groupBy) return persistedState.groupBy;
    return groupByProp || null;
  });
  // Sync from props if the parent updates them externally (e.g. saved
  // view in server-mode).
  useEffect(() => {
    if (!serverMode) return;
    const incoming = groupByProp ?? queryProp?.groupBy ?? null;
    setGroupBy((prev) => (prev === incoming ? prev : incoming));
  }, [serverMode, groupByProp, queryProp?.groupBy]);

  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    if (serverMode) return queryProp?.advancedFilters || null;
    if (gridId && persistedState.advancedFilters) return persistedState.advancedFilters;
    return null;
  });

  // Server-mode sync — when the parent updates `query.advancedFilters`
  // externally (e.g. loading a saved view), reflect it into the grid's
  // local state so the toolbar badge + modal seed stay accurate. The
  // local state is intentionally the single source of truth for the
  // modal so click-Apply latency is zero — this effect only handles
  // *external* changes from the parent.
  useEffect(() => {
    if (!serverMode) return;
    const incoming = queryProp?.advancedFilters || null;
    setAdvancedFilters((prev) => {
      if (prev === incoming) return prev;
      // Cheap structural check to avoid re-renders when the same
      // group identity is passed through repeatedly.
      try {
        if (JSON.stringify(prev) === JSON.stringify(incoming)) return prev;
      } catch (_e) {
        /* noop — fall through to assignment */
      }
      return incoming;
    });
  }, [serverMode, queryProp?.advancedFilters]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [modal, setModal] = useState({ open: false, mode: null, record: null });
  const [drawer, setDrawer] = useState({ open: false, record: null });
  const tableRef = useRef(null);
  const searchInputRef = useRef(null);
  const isMac = useMemo(() => detectMac(), []);
  const kbdHint = searchShortcut ? (isMac ? '⌘K' : 'Ctrl K') : null;

  // ── derived ───────────────────────────────────────────────────
  const density = ROW_DENSITY[rowHeight] || ROW_DENSITY.compact;
  const resolvedSize = size || density.size;
  const leafColumns = useMemo(() => flattenColumns(columns), [columns]);

  // Auto-pick searchable fields when caller doesn't specify.
  const effectiveSearchFields = useMemo(() => {
    if (Array.isArray(searchableFields)) return searchableFields;
    return leafColumns.map((c) => c.dataIndex).filter(Boolean);
  }, [leafColumns, searchableFields]);

  // ── search filter ─────────────────────────────────────────────
  // In server mode the parent already filters/sorts/pages on the
  // backend — `dataSource` IS the current page and we paint it verbatim.
  //
  // Client mode applies (in order):
  //   1. text-search (`searchText`) over `effectiveSearchFields`
  //   2. column-level filter dropdowns (handled by AntD natively)
  //   3. advanced-filter group via evaluateAdvancedFilters
  //   4. multi-column sort via applyClientSort
  const filteredData = useMemo(() => {
    if (!Array.isArray(dataSource)) return [];
    if (serverMode) return dataSource;
    let rows = dataSource;
    if (searchText) {
      const needle = String(searchText).trim().toLowerCase();
      if (needle) {
        rows = rows.filter((row) =>
          effectiveSearchFields.some((field) => {
            const v = readField(row, field);
            if (v === null || v === undefined) return false;
            return String(v).toLowerCase().includes(needle);
          })
        );
      }
    }
    if (advancedFilters && Array.isArray(advancedFilters.rules) && advancedFilters.rules.length) {
      rows = rows.filter((r) => evaluateAdvancedFilters(r, advancedFilters));
    }
    // Translate AntD's sorter object into our GridSortSpec[] so the
    // same applyClientSort routine works for AntD and our own emitter.
    const antdSorter = tableState.sorter;
    if (antdSorter) {
      const arr = Array.isArray(antdSorter) ? antdSorter : [antdSorter];
      const sort = arr
        .filter((s) => s && s.field && s.order)
        .map((s) => ({
          field: Array.isArray(s.field) ? s.field.join('.') : String(s.field),
          direction: s.order === 'descend' ? 'desc' : 'asc'
        }));
      if (sort.length) rows = applyClientSort(rows, sort);
    }
    return rows;
  }, [
    dataSource,
    searchText,
    effectiveSearchFields,
    serverMode,
    advancedFilters,
    tableState.sorter
  ]);

  // ── permission / action visibility ────────────────────────────
  const showActionColumn = useMemo(() => {
    return (
      (actions.view  && permissions.view  !== false) ||
      (actions.edit  && permissions.edit  !== false) ||
      (actions.delete && permissions.delete !== false)
    );
  }, [actions, permissions]);

  const canAdd = Boolean(
    actions.add &&
      permissions.add !== false &&
      (onAdd || (renderForm && onSubmit))
  );
  const canBulkDelete = Boolean(
    actions.delete && permissions.delete !== false && onBulkDelete
  );

  // ── modal / drawer helpers ───────────────────────────────────
  const openModal = useCallback((mode, record = null) => {
    setModal({ open: true, mode, record });
  }, []);
  const closeModal = useCallback(() => {
    setModal((m) => ({ ...m, open: false }));
  }, []);
  const openDrawer = useCallback((record) => {
    setDrawer({ open: true, record });
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawer((d) => ({ ...d, open: false }));
  }, []);

  // ── CRUD click handlers ──────────────────────────────────────
  const handleAdd = useCallback(() => {
    if (typeof onAdd === 'function') return onAdd();
    if (renderForm && onSubmit) return openModal('add');
  }, [onAdd, renderForm, onSubmit, openModal]);

  const handleView = useCallback(
    (record) => {
      if (typeof onView === 'function') return onView(record);
      // Prefer the read-only drawer when the grid is configured for
      // detail-groups — keeps the View affordance consistent with the
      // double-click row gesture. Falls back to the form modal in
      // 'view' mode for grids that don't define detailGroups.
      if (Array.isArray(detailGroups) && detailGroups.length) {
        return openDrawer(record);
      }
      if (renderForm) return openModal('view', record);
    },
    [onView, detailGroups, openDrawer, renderForm, openModal]
  );

  const handleEdit = useCallback(
    (record) => {
      if (typeof onEdit === 'function') return onEdit(record);
      if (renderForm && onSubmit) return openModal('edit', record);
    },
    [onEdit, renderForm, onSubmit, openModal]
  );

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: 'Delete this record?',
        icon: <ExclamationCircleOutlined className="!text-rose-500" />,
        content:
          'This action cannot be undone. Are you sure you want to continue?',
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        maskClosable: true,
        autoFocusButton: 'cancel',
        onOk: async () => {
          if (typeof onDelete === 'function') {
            await onDelete(record);
          }
        }
      });
    },
    [onDelete]
  );

  // ── action column injection ──────────────────────────────────
  // Single ⋯ Dropdown trigger per row. View / Edit / Delete live
  // inside the menu so the action column is compact (~56 px) and
  // consistent for all ERP modules. Wrapper stops click + dblclick
  // from bubbling so the row's double-click-to-open gesture never
  // fires when the user is targeting an action.
  const actionColumn = useMemo(() => {
    if (!showActionColumn) return null;
    // Default header is a centered gear icon. Caller can override
    // with any string / JSX via `actionsLabel`, or pass `''` for a
    // blank header cell.
    const resolvedTitle =
      actionsLabel === undefined ? (
        <SettingOutlined
          aria-hidden="true"
          className="erp-grid__actions-header-icon"
        />
      ) : (
        actionsLabel
      );
    // Inline cell style clamps the rendered width to exactly
    // `actionWidth` px. AntD treats `column.width` as a hint when
    // `scroll.x` is active (the column can stretch); the
    // width/minWidth/maxWidth triple here removes that slack and
    // forces the same width on header AND body rows.
    const cellStyle = {
      width: actionWidth,
      minWidth: actionWidth,
      maxWidth: actionWidth
    };
    return {
      title: resolvedTitle,
      key: '__erp_actions__',
      width: actionWidth,
      fixed: stickyActions ? 'right' : undefined,
      align: 'center',
      className: 'erp-grid__actions-cell',
      // No empty fallback here — the cell is purely interactive.
      emptyFallback: false,
      onCell: () => ({ style: cellStyle }),
      onHeaderCell: () => ({ style: cellStyle }),
      render: (_v, record) => {
        const showView = actions.view   && resolveAction(permissions.view,   record);
        const showEdit = actions.edit   && resolveAction(permissions.edit,   record);
        const showDel  = actions.delete && resolveAction(permissions.delete, record);
        if (!showView && !showEdit && !showDel) {
          return <span className="text-ink-300">—</span>;
        }
        const items = [];
        if (showView) {
          items.push({
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View details',
            onClick: () => handleView(record)
          });
        }
        if (showEdit) {
          items.push({
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Edit',
            onClick: () => handleEdit(record)
          });
        }
        if (showDel) {
          if (items.length > 0) {
            items.push({ type: 'divider', key: 'sep-del' });
          }
          items.push({
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            onClick: () => handleDelete(record)
          });
        }
        return (
          <div
            className="erp-grid__actions"
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              placement="bottomRight"
              overlayClassName="erp-grid__action-menu"
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                aria-label="Row actions"
                className="erp-grid__action-trigger"
              />
            </Dropdown>
          </div>
        );
      }
    };
  }, [
    showActionColumn,
    actions,
    permissions,
    actionsLabel,
    actionWidth,
    stickyActions,
    handleView,
    handleEdit,
    handleDelete
  ]);

  // ── visible columns (reorder + prune + inline-edit + multi-sort) ─
  // Pipeline order matters; each pass is a pure transform:
  //   1. reorder    — apply the persisted column order (top-level only)
  //   2. prune      — drop hidden + collapse now-empty parent groups
  //   3. width      — overlay persisted column widths on top of declared widths
  //   4. inlineEdit — wrap editable columns in ErpGridEditableCell
  //   5. multiSort  — auto-assign sorter.multiple priorities so AntD enables shift-sort
  //   6. tooltip    — overflow ellipsis tooltip
  //   7. empty      — muted em-dash fallback
  //
  // `orderedColumnKeys` must be declared before `reorderedColumns` —
  // hook calls execute top-down, and reading it later would TDZ-throw.
  // `handleColumnMove` (further down) consumes the same memo.
  const orderedColumnKeys = useMemo(() => {
    return Array.isArray(columnOrder) ? columnOrder : [];
  }, [columnOrder]);

  const reorderedColumns = useMemo(
    () => applyColumnOrder(columns || [], orderedColumnKeys),
    [columns, orderedColumnKeys]
  );

  const visibleColumns = useMemo(
    () => pruneColumns(reorderedColumns, hiddenColumnKeys),
    [reorderedColumns, hiddenColumnKeys]
  );

  // Apply persisted column widths as an override of declared widths.
  // Pure column-tree walk; only modifies columns whose key matches a
  // persisted width entry.
  const widthOverrideColumns = useMemo(() => {
    if (!columnWidths || Object.keys(columnWidths).length === 0) {
      return visibleColumns;
    }
    const walk = (cols) =>
      (cols || []).map((col) => {
        if (!col) return col;
        if (Array.isArray(col.children) && col.children.length) {
          return { ...col, children: walk(col.children) };
        }
        const key = col.key || col.dataIndex;
        const w = columnWidths[key];
        return Number.isFinite(w) ? { ...col, width: w } : col;
      });
    return walk(visibleColumns);
  }, [visibleColumns, columnWidths]);

  // Inline-edit wrapper. Only kicks in when:
  //   - parent provides `onUpdateCell`
  //   - column declares `editable: true`
  //   - column NOT locked (unless allowLockedEdit: true)
  // Otherwise the column passes through verbatim.
  const editableColumns = useMemo(() => {
    if (typeof onUpdateCell !== 'function') return widthOverrideColumns;
    const walk = (cols) =>
      (cols || []).map((col) => {
        if (!col) return col;
        if (Array.isArray(col.children) && col.children.length) {
          return { ...col, children: walk(col.children) };
        }
        if (!col.editable) return col;
        if (col.locked && col.allowLockedEdit !== true) return col;
        const userRender = col.render;
        const dataIndex = col.dataIndex;
        return {
          ...col,
          render: (value, record, index) => {
            const allowed =
              typeof canEditCell === 'function' ? canEditCell(record, col) : true;
            return (
              <ErpGridEditableCell
                value={value}
                record={record}
                index={index}
                column={col}
                renderRead={userRender}
                permissionToEdit={allowed}
                onSave={async (newValue) => {
                  await onUpdateCell(record, dataIndex, newValue, value);
                }}
              />
            );
          }
        };
      });
    return walk(widthOverrideColumns);
  }, [widthOverrideColumns, onUpdateCell, canEditCell]);

  // Multi-sort priorities. AntD enables shift+click multi-column
  // sort when sortable columns expose a numeric `sorter.multiple`.
  // We auto-assign a stable priority (column index + 1) to every
  // column whose `sorter` was declared as a function — that's the
  // typical shape ERP modules use. Columns that already set
  // `sorter.multiple` are left untouched.
  const multiSortColumns = useMemo(() => {
    if (!multiSort) return editableColumns;
    let counter = 0;
    const walk = (cols) =>
      (cols || []).map((col) => {
        if (!col) return col;
        if (Array.isArray(col.children) && col.children.length) {
          return { ...col, children: walk(col.children) };
        }
        if (!col.sorter) return col;
        const idx = ++counter;
        if (typeof col.sorter === 'function') {
          return { ...col, sorter: { compare: col.sorter, multiple: idx } };
        }
        if (typeof col.sorter === 'object' && col.sorter.multiple === undefined) {
          return { ...col, sorter: { ...col.sorter, multiple: idx } };
        }
        return col;
      });
    return walk(editableColumns);
  }, [editableColumns, multiSort]);

  const mergedColumns = useMemo(() => {
    const all = actionColumn
      ? [...(multiSortColumns || []), actionColumn]
      : multiSortColumns || [];
    // Order matters: overflow-tooltip wraps the user's render first so
    // it sees the original value (and can decide whether to show a
    // tooltip), then withEmptyFallback wraps that to produce the muted
    // em-dash for empty cells.
    const withTooltip = withOverflowTooltip(all);
    const withFallback = emptyFallback ? withEmptyFallback(withTooltip) : withTooltip;
    // Final pass: when grouping is active and we actually have group
    // headers to render, collapse every data-column cell on a header
    // row into a single wide cell. The aggregate-bearing column list
    // is passed in so the header renderer can compute (or fall back to
    // server-supplied) per-group totals.
    //
    // Client mode: pass enabled whenever `groupBy` is set (the page
    // slice in pagedData injects pseudo-rows).
    // Server mode: pass enabled only when the backend also returned
    // `meta.groups`; otherwise the rows are flat and we shouldn't
    // colspan anything.
    const hasGroupHeaders =
      groupBy &&
      (!serverMode || (meta && Array.isArray(meta.groups) && meta.groups.length > 0));
    if (!hasGroupHeaders) return withFallback;
    const aggCols = flattenColumns(withFallback).filter(
      (c) => c && c.aggregate && c.key !== '__erp_actions__'
    );
    return withGroupHeaderRendering(withFallback, aggCols);
  }, [multiSortColumns, actionColumn, emptyFallback, groupBy, serverMode, meta]);

  // ── pagination — disabled on the Table; rendered as a custom
  //    footer with icon-only navigation (|◀ ◀ ▶ ▶|).
  //
  // Client mode: we slice the visible rows manually so the Table
  // only paints the current page; selection / filtering / sorting
  // operate on `filteredData` before slicing.
  //
  // Server mode: `dataSource` IS the current page; we don't slice.
  // The pagination footer's total comes from `totalProp` (the
  // backend total), and page/pageSize changes are forwarded through
  // `onQueryChange`.
  const usePagination = pagination !== false;
  const currentPage = serverMode
    ? queryProp?.page || tableState.pagination.current || 1
    : tableState.pagination.current || 1;
  const currentPageSize = serverMode
    ? queryProp?.pageSize || tableState.pagination.pageSize || pageSize
    : tableState.pagination.pageSize || pageSize;

  const pagedData = useMemo(() => {
    // Client-mode: pagination slice runs FIRST, then grouping inserts
    // headers among only the rows visible on the current page. This
    // keeps page counts predictable (page-size 25 → 25 data rows + a
    // few header pseudo-rows; user understands the "data row" budget).
    let rows;
    if (serverMode) {
      rows = filteredData;
    } else if (!usePagination) {
      rows = filteredData;
    } else {
      const start = (currentPage - 1) * currentPageSize;
      rows = filteredData.slice(start, start + currentPageSize);
    }
    if (!serverMode && groupBy) {
      const { rows: grouped } = groupRowsByField(rows, groupBy);
      return grouped;
    }
    if (serverMode && groupBy && meta && Array.isArray(meta.groups) && meta.groups.length > 0) {
      // Server has already aggregated — interleave its group headers
      // with the rows it just returned. Falls back to flat rows when
      // meta.groups is absent (the existing zero-meta server path).
      const { rows: grouped } = buildGroupedRowsFromServerMeta(rows, meta, groupBy);
      return grouped;
    }
    return rows;
  }, [serverMode, usePagination, filteredData, currentPage, currentPageSize, groupBy, meta]);

  const handlePaginationChange = useCallback(
    (page, size) => {
      if (serverMode) {
        if (typeof onQueryChange === 'function') {
          onQueryChange({ page, pageSize: size });
        }
        return;
      }
      setTableState((s) => ({
        ...s,
        pagination: { current: page, pageSize: size }
      }));
      // Persist page size only — page index is transient.
      if (gridId && size !== currentPageSize) {
        persistence.setPersisted({ pageSize: size });
      }
    },
    [serverMode, onQueryChange, gridId, currentPageSize, persistence]
  );

  // Clamp current page back into range when filteredData shrinks
  // (e.g., after a search narrows the rowset). Server mode skips this
  // — the backend total drives the footer; the parent decides when to
  // wind the page back.
  useEffect(() => {
    if (!usePagination || serverMode) return;
    const lastPage = Math.max(1, Math.ceil(filteredData.length / currentPageSize));
    if (currentPage > lastPage) {
      setTableState((s) => ({
        ...s,
        pagination: { current: lastPage, pageSize: s.pagination.pageSize }
      }));
    }
  }, [filteredData.length, currentPage, currentPageSize, usePagination, serverMode]);

  // ── scroll defaults ──────────────────────────────────────────
  // `x: 'max-content'` lets AntD compute the natural total width from
  // column `width` props. The columns demo sums to ~1280 px which
  // forces horizontal scroll inside narrower viewports rather than
  // collapsing intermediate columns and creating dead gaps.
  //
  // Virtualization (when enabled) requires a numeric `y`. We supply a
  // pragmatic default the caller can override via the `scroll` prop.
  const resolvedScroll = useMemo(() => {
    const base = { x: 'max-content' };
    const merged = scroll ? { ...base, ...scroll } : base;
    if (virtualized && merged.y == null) {
      merged.y = 480; // sane default — caller should override per page.
    }
    return merged;
  }, [scroll, virtualized]);

  // ── virtualization gate ──────────────────────────────────────
  // AntD 5's `virtual` Table is incompatible with several features
  // that ERP grids commonly use. We respect the caller's intent but
  // auto-disable when an active feature would break the layout. The
  // gate is conservative — opt out only by silently degrading.
  const effectiveVirtualized = useMemo(() => {
    if (!virtualized) return false;
    if (expandable) return false;            // expandable rows + virtual = AntD known issue
    if (groupBy && !serverMode) return false; // group-header colSpan ≠ virtual rows
    // Server-mode grouping ALSO injects pseudo-rows when meta.groups
    // is supplied, so disable virtualization in that case too.
    if (groupBy && serverMode && meta && Array.isArray(meta.groups) && meta.groups.length > 0) {
      return false;
    }
    return true;
  }, [virtualized, expandable, groupBy, serverMode, meta]);

  // ── table change handler ─────────────────────────────────────
  const handleTableChange = useCallback(
    (pag, fil, sort) => {
      setTableState({
        pagination: { current: pag.current, pageSize: pag.pageSize },
        filters: fil || {},
        sorter: sort || {}
      });
      // Server mode forwards sort/filter to the backend as one patch.
      if (serverMode && typeof onQueryChange === 'function') {
        const patch = fromAntdTableChange(pag, fil, sort);
        onQueryChange(patch);
      }
    },
    [serverMode, onQueryChange]
  );

  // ── selection wiring ─────────────────────────────────────────
  // `getCheckboxProps` is a passthrough — when the caller supplies one
  // we honour it AND chain our group-header disable so users can't
  // tick a pseudo-row checkbox by mistake.
  const resolvedRowSelection = useMemo(() => {
    if (!selectable && !rowSelection) return undefined;
    const userCfg = rowSelection && typeof rowSelection === 'object' ? rowSelection : {};
    const userGetCheckboxProps = userCfg.getCheckboxProps;
    return {
      type: 'checkbox',
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
      preserveSelectedRowKeys: true,
      columnWidth: 44,
      fixed: true, // pin the checkbox column so it stays put on h-scroll
      ...userCfg,
      getCheckboxProps: (record) => {
        const base = typeof userGetCheckboxProps === 'function'
          ? userGetCheckboxProps(record) || {}
          : {};
        if (isGroupHeaderRow(record)) {
          return { ...base, disabled: true, style: { ...(base.style || {}), visibility: 'hidden' } };
        }
        return base;
      }
    };
  }, [selectable, rowSelection, selectedRowKeys]);

  const selectedRecords = useMemo(() => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) return [];
    const keyOf =
      typeof rowKey === 'function'
        ? rowKey
        : (r) => (r ? r[rowKey] : undefined);
    const lookup = new Map();
    (dataSource || []).forEach((r) => {
      const k = keyOf(r);
      if (k !== undefined) lookup.set(k, r);
    });
    return selectedRowKeys
      .map((k) => lookup.get(k))
      .filter((r) => r !== undefined);
  }, [selectedRowKeys, dataSource, rowKey]);

  // Clear stale keys when the dataSource shrinks (e.g., after a delete).
  useEffect(() => {
    if (selectedRowKeys.length === 0) return;
    const keyOf =
      typeof rowKey === 'function'
        ? rowKey
        : (r) => (r ? r[rowKey] : undefined);
    const validKeys = new Set((dataSource || []).map(keyOf));
    const next = selectedRowKeys.filter((k) => validKeys.has(k));
    if (next.length !== selectedRowKeys.length) setSelectedRowKeys(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  // ── clear filters / search / sort / selection ────────────────
  const clearFilters = useCallback(() => {
    setSearchText('');
    setTableState({
      pagination: { current: 1, pageSize },
      filters: {},
      sorter: {}
    });
  }, [pageSize]);

  const clearOneFilter = useCallback((key) => {
    setTableState((s) => {
      const next = { ...s.filters };
      delete next[key];
      return { ...s, filters: next };
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedRowKeys([]), []);

  const handleBulkDelete = useCallback(
    async (keys, records) => {
      if (typeof onBulkDelete === 'function') {
        await onBulkDelete(keys, records);
        setSelectedRowKeys([]);
      }
    },
    [onBulkDelete]
  );

  // ── column visibility ────────────────────────────────────────
  const handleColumnVisibilityChange = useCallback(
    (key, shouldHide) => {
      setHiddenColumnKeys((prev) => {
        const next = new Set(prev);
        if (shouldHide) next.add(key);
        else next.delete(key);
        if (gridId) {
          persistence.setPersisted({ hiddenColumnKeys: Array.from(next) });
        }
        return next;
      });
    },
    [gridId, persistence]
  );

  // Reset layout — clear persisted state for this gridId AND reset
  // live state so the change is visible immediately.
  const handleResetLayout = useCallback(() => {
    if (!gridId) return;
    persistence.reset();
    setHiddenColumnKeys(new Set());
    setColumnOrder([]);
    setColumnWidths({});
    setAdvancedFilters(null);
    setGroupBy(null);
    setSearchText('');
    setTableState({
      pagination: { current: 1, pageSize },
      filters: {},
      sorter: {}
    });
    if (serverMode && typeof onQueryChange === 'function') {
      onQueryChange({
        page: 1,
        pageSize,
        search: '',
        sort: [],
        filters: [],
        advancedFilters: null,
        groupBy: null
      });
    }
  }, [gridId, persistence, pageSize, serverMode, onQueryChange]);

  // Group-by change handler — persists, forwards to server-mode parent,
  // and clears any active row selection so the bulk bar doesn't carry
  // selections across grouping transitions.
  const handleGroupByChange = useCallback(
    (nextKey) => {
      setGroupBy(nextKey || null);
      if (gridId) persistence.setPersisted({ groupBy: nextKey || null });
      if (serverMode && typeof onQueryChange === 'function') {
        onQueryChange({ groupBy: nextKey || null, page: 1 });
      }
      setSelectedRowKeys([]);
    },
    [gridId, persistence, serverMode, onQueryChange]
  );

  // ── column reorder ──────────────────────────────────────────
  // Works for both flat AND grouped column configs. Moves are scoped
  // to the column's own sibling array — e.g. reordering "GSTIN"
  // inside the "Customer Details" group cannot accidentally pull it
  // out of the group and break colspan headers.
  //
  // Implementation:
  //   1. find the column + its sibling array in the reordered tree
  //   2. swap with the adjacent sibling (refusing fixed-left/right
  //      boundary crossings)
  //   3. persist a flat global leaf-key list — applyColumnOrder uses
  //      it to re-sort every sibling array consistently.
  // `orderedColumnKeys` is declared above (next to `reorderedColumns`)
  // because the render pipeline needs it earlier in the hook order.
  const handleColumnMove = useCallback(
    (key, delta) => {
      // Resolve the current effective tree (with any prior reorder
      // already applied) and find the affected siblings.
      const effective = applyColumnOrder(columns || [], orderedColumnKeys);
      const found = findColumnAndSiblings(effective, key);
      if (!found) return;
      const { siblings, index } = found;
      const targetIdx = index + delta;
      if (targetIdx < 0 || targetIdx >= siblings.length) return;

      const a = siblings[index];
      const b = siblings[targetIdx];
      // Defensive fixed-left/right boundary rules.
      const fa = a?.fixed;
      const fb = b?.fixed;
      if (fa === 'left' && fb !== 'left' && delta > 0) return;
      if (fa !== 'left' && fb === 'left' && delta < 0) return;
      if (fa === 'right' && fb !== 'right' && delta < 0) return;
      if (fa !== 'right' && fb === 'right' && delta > 0) return;

      // Build a NEW flat-leaf order: swap the two adjacent sibling
      // spans inside the current effective leaf sequence. Group
      // headers carry no own key — when we move a group sibling we
      // move ALL of its leaf children as a single block, keeping the
      // colspan structure coherent.
      const flat = flattenColumnKeys(effective);
      const aKeys = Array.isArray(a?.children) && a.children.length
        ? flattenColumnKeys([a])
        : [a?.key || a?.dataIndex].filter(Boolean);
      const bKeys = Array.isArray(b?.children) && b.children.length
        ? flattenColumnKeys([b])
        : [b?.key || b?.dataIndex].filter(Boolean);
      const aStart = flat.indexOf(aKeys[0]);
      const bStart = flat.indexOf(bKeys[0]);
      if (aStart < 0 || bStart < 0) return;
      // The two sibling spans are guaranteed adjacent in the flat
      // sequence (siblings of the same parent render consecutively),
      // so we splice them out and concat in swapped order.
      const left = Math.min(aStart, bStart);
      const right = Math.max(aStart + aKeys.length, bStart + bKeys.length);
      const before = flat.slice(0, left);
      const after = flat.slice(right);
      const newFlat =
        aStart < bStart
          ? [...before, ...bKeys, ...aKeys, ...after]
          : [...before, ...aKeys, ...bKeys, ...after];
      setColumnOrder(newFlat);
      if (gridId) persistence.setPersisted({ columnOrder: newFlat });
    },
    [columns, orderedColumnKeys, gridId, persistence]
  );

  const handleColumnMoveUp = useCallback(
    (key) => handleColumnMove(key, -1),
    [handleColumnMove]
  );
  const handleColumnMoveDown = useCallback(
    (key) => handleColumnMove(key, +1),
    [handleColumnMove]
  );

  // ── advanced filter apply ───────────────────────────────────
  const handleAdvancedFilterApply = useCallback(
    (group) => {
      setAdvancedFilters(group);
      if (gridId) {
        // Persist alongside other UI prefs. Group is small (rule
        // count is bounded by the modal UX) and contains no row data.
        persistence.setPersisted({ advancedFilters: group });
      }
      if (serverMode && typeof onQueryChange === 'function') {
        onQueryChange({ advancedFilters: group, page: 1 });
      }
      // Reset to page 1 in client mode too so the user sees results.
      if (!serverMode) {
        setTableState((s) => ({
          ...s,
          pagination: { current: 1, pageSize: s.pagination.pageSize }
        }));
      }
    },
    [gridId, persistence, serverMode, onQueryChange]
  );

  // ── CSV export ───────────────────────────────────────────────
  // Shared CSV writer used by both "current view" and "selected rows"
  // export paths so the column order, headers, and quoting rules
  // stay identical between the two surfaces.
  const writeCsv = useCallback(
    (rowsToExport, fileSuffix) => {
      const visibleLeaves = flattenColumns(visibleColumns).filter(
        (c) => c.exportable !== false
      );
      if (!visibleLeaves.length) return;
      const header = visibleLeaves.map((c) =>
        csvCell(typeof c.title === 'string' ? c.title : c.key || c.dataIndex)
      );
      const lines = (rowsToExport || []).map((row) =>
        visibleLeaves.map((c) => csvCell(readField(row, c.dataIndex))).join(',')
      );
      const csv = [header.join(','), ...lines].join('\r\n');
      const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      const base =
        exportFilename ||
        (title ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'records');
      a.href = url;
      a.download = `${base}-${fileSuffix}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    [visibleColumns, title, exportFilename]
  );

  const handleExport = useCallback(
    () => writeCsv(filteredData, 'view'),
    [writeCsv, filteredData]
  );

  const handleExportSelected = useCallback(() => {
    if (!selectedRecords || selectedRecords.length === 0) return;
    writeCsv(selectedRecords, 'selected');
  }, [writeCsv, selectedRecords]);

  // ── keyboard shortcut: focus search ──────────────────────────
  useEffect(() => {
    if (!searchShortcut || !searchable) return undefined;
    const onKeyDown = (e) => {
      const k = (e.key || '').toLowerCase();
      const isCmd = isMac ? e.metaKey : e.ctrlKey;
      if (isCmd && k === 'k') {
        // Skip if focus is in another input — let browser/native shortcuts win.
        const ae = document.activeElement;
        if (
          ae &&
          (ae.tagName === 'INPUT' ||
            ae.tagName === 'TEXTAREA' ||
            ae.isContentEditable) &&
          ae !== (searchInputRef.current && searchInputRef.current.input)
        ) {
          // Allow shortcut only when typing isn't in another input.
          // (We still focus on Cmd/Ctrl+K from a different input — many
          // power users expect this. So we don't return here.)
        }
        e.preventDefault();
        if (searchInputRef.current && typeof searchInputRef.current.focus === 'function') {
          searchInputRef.current.focus({ cursor: 'all' });
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [searchShortcut, searchable, isMac]);

  // ── built-in form submit pass-through ────────────────────────
  const handleFormSubmit = useCallback(
    async (values) => {
      if (!onSubmit) return;
      await onSubmit(modal.mode, values, modal.record || null);
      closeModal();
    },
    [onSubmit, modal.mode, modal.record, closeModal]
  );

  // ── Stage 6A.2G — context menu state ──────────────────────────
  // Tracks the right-clicked row + viewport-relative coordinates so
  // the AntD Dropdown can render at the click point. `null` means the
  // menu is closed. Close on outside-click / Escape / scroll / page
  // change is wired below via effects.
  const [contextMenu, setContextMenu] = useState(null);
  const hasContextMenu = Array.isArray(contextMenuActions) && contextMenuActions.length > 0;
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeContextMenu(); };
    const onScroll = () => closeContextMenu();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [contextMenu, closeContextMenu]);

  // ── row DOUBLE-click → drawer ────────────────────────────────
  // Single click does nothing (selection happens through the
  // checkbox column only). Double-click anywhere on the row, except
  // the checkbox / action cell / any embedded button, opens the
  // read-only detail drawer (or fires the caller's `onRowClick`).
  // This avoids accidental opens during normal row scanning.
  const hasRowClick = Boolean(
    onRowClick || (Array.isArray(detailGroups) && detailGroups.length)
  );
  const onRow = useMemo(() => {
    if (!hasRowClick && !hasContextMenu) return undefined;
    return (record) => {
      const handlers = {};
      if (hasRowClick) {
        handlers.onDoubleClick = (e) => {
          // Group-header pseudo-rows must NOT open the detail drawer or
          // fire onRowClick — they carry no real customer payload.
          if (record && (record.__erpGroupHeader || record.isGroupHeader)) return;
          if (isInteractiveRowClick(e)) return;
          if (typeof onRowClick === 'function') {
            onRowClick(record);
          } else {
            openDrawer(record);
          }
        };
      }
      if (hasContextMenu) {
        handlers.onContextMenu = (e) => {
          // Stage 6A.2G — context menu only fires on real data rows.
          // Group-header / summary / empty-state / header / pagination
          // rows are filtered out:
          //   • Group headers come from gridQuery.buildGroupedRowsFromServerMeta
          //     and carry isGroupHeader=true; we skip those.
          //   • Empty / summary / pagination DOM lives outside <tbody>
          //     so `onRow` never fires for them.
          //   • Inline-edit interactives are filtered the same way as
          //     the double-click handler so clicks inside an editor or
          //     action button don't open the menu.
          if (record && record.isGroupHeader) return;
          if (isInteractiveRowClick(e)) return;
          e.preventDefault();
          setContextMenu({
            record,
            x: e.clientX,
            y: e.clientY
          });
        };
      }
      return handlers;
    };
  }, [hasRowClick, hasContextMenu, onRowClick, openDrawer]);

  // ── presentation switch ──────────────────────────────────────
  // Error always wins; then initial-load skeleton; then normal table
  // (AntD's spinner overlay handles "refreshing existing data").
  const errorMessage =
    error && typeof error === 'object' && error.message
      ? error.message
      : typeof error === 'string'
        ? error
        : null;
  const showError = Boolean(error);
  const showInitialSkeleton =
    !showError &&
    loading &&
    (!Array.isArray(dataSource) || dataSource.length === 0);

  // Drawer title / subtitle — resolve from prop or callable.
  const resolveDrawerCopy = (v, record) =>
    typeof v === 'function' ? v(record) : v;

  // External rowClassName passes through unmodified. We deliberately
  // don't add a `cursor: pointer` marker — single click does nothing
  // (row-open is a double-click gesture), so a pointer cursor would
  // mislead users into thinking one click would do something.
  const composedRowClassName = rowClassName;

  // ── sticky offset wiring ─────────────────────────────────────
  // AntD's <Table sticky> takes a static number for `offsetHeader`.
  // When the toolbar is also sticky, the thead must pin BELOW it,
  // hence offset = stickyOffset + estimated toolbar strip height.
  // The toolbar height is normalised in CSS via a min-height token
  // so this stays a stable number across modules.
  const TOOLBAR_STRIP_HEIGHT = 56;
  const effectiveStickyOffset =
    stickyHeader
      ? stickyOffset + (stickyToolbar && toolbar ? TOOLBAR_STRIP_HEIGHT : 0)
      : 0;

  // Inline CSS custom property so the toolbar's sticky `top` honours
  // the same `stickyOffset` the caller supplied for the AntD thead.
  const gridStyle = useMemo(() => ({
    '--erp-grid-sticky-top': `${stickyOffset}px`
  }), [stickyOffset]);

  // ── render ───────────────────────────────────────────────────
  return (
    <div
      className={`erp-grid erp-grid--theme-${theme} ${density.className} ${className} ${
        stickyToolbar && toolbar ? 'erp-grid--sticky-toolbar' : ''
      }`}
      style={gridStyle}
    >
      {toolbar ? (
        <>
          <ErpDataGridToolbar
            title={title}
            searchable={searchable}
            searchValue={searchText}
            searchPlaceholder={searchPlaceholder}
            searchInputRef={searchInputRef}
            onSearchChange={setSearchText}
            kbdHint={kbdHint}
            canAdd={canAdd}
            onAdd={handleAdd}
            addButtonText={addButtonText}
            onRefresh={onRefresh}
            onClearFilters={clearFilters}
            leafColumns={leafColumns}
            hiddenColumnKeys={hiddenColumnKeys}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            exportable={exportable}
            onExport={exportable ? handleExport : undefined}
            onExportSelected={
              exportable && selectedRecords.length > 0
                ? handleExportSelected
                : undefined
            }
            selectedCount={selectedRowKeys.length}
            onResetLayout={gridId ? handleResetLayout : undefined}
            onColumnMoveUp={gridId ? handleColumnMoveUp : undefined}
            onColumnMoveDown={gridId ? handleColumnMoveDown : undefined}
            onAdvancedFilter={() => setAdvancedFilterOpen(true)}
            advancedFilterCount={countAdvancedFilterRules(advancedFilters)}
            groupableColumns={
              // Surface only columns explicitly marked `groupable: true`
              // and visible (we don't want the dropdown listing a column
              // the user hid via the column picker).
              leafColumns.filter(
                (c) =>
                  c.groupable === true &&
                  c.key !== '__erp_actions__' &&
                  !hiddenColumnKeys.has(c.key || c.dataIndex)
              )
            }
            groupByKey={groupBy}
            onGroupByChange={handleGroupByChange}
            extra={toolbarExtra}
            totalRows={
              serverMode
                ? Number.isFinite(totalProp)
                  ? totalProp
                  : 0
                : Array.isArray(dataSource)
                  ? dataSource.length
                  : 0
            }
            filteredRows={Array.isArray(filteredData) ? filteredData.length : 0}
          />
          {description ? (
            <p className="erp-grid__description">{description}</p>
          ) : null}
          <ErpDataGridFilterChips
            filters={tableState.filters}
            searchText={searchText}
            leafColumns={leafColumns}
            onClearFilter={clearOneFilter}
            onClearSearch={() => setSearchText('')}
            onClearAll={clearFilters}
          />
          <ErpDataGridBulkBar
            selectedRowKeys={selectedRowKeys}
            selectedRecords={selectedRecords}
            onClearSelection={clearSelection}
            bulkActions={bulkActions}
            onBulkDelete={handleBulkDelete}
            canDelete={canBulkDelete}
          />
        </>
      ) : null}

      {showError ? (
        <ErpDataGridError message={errorMessage} onRetry={onRefresh} />
      ) : showInitialSkeleton ? (
        <ErpDataGridSkeleton
          rows={Math.min(8, tableState.pagination.pageSize || 8)}
          columns={Math.min(8, (leafColumns || []).length || 6)}
        />
      ) : (
        <Table
          ref={tableRef}
          rowKey={rowKey}
          size={resolvedSize}
          bordered={bordered}
          sticky={stickyHeader ? { offsetHeader: effectiveStickyOffset } : false}
          loading={loading}
          columns={mergedColumns}
          dataSource={pagedData}
          rowSelection={resolvedRowSelection}
          pagination={false}
          scroll={resolvedScroll}
          virtual={effectiveVirtualized}
          onChange={handleTableChange}
          onRow={onRow}
          expandable={expandable}
          locale={{
            emptyText: (
              <ErpDataGridEmpty
                title={emptyTitle || 'No records to display'}
                description={emptyText}
                ctaLabel={canAdd ? emptyCtaLabel || addButtonText : undefined}
                onCtaClick={canAdd ? handleAdd : undefined}
              />
            )
          }}
          summary={
            // Summary footer renders only when at least one visible
            // leaf column declares an `aggregate` kind OR the server
            // sent back a `summary` map. Otherwise the footer slot
            // returns null and the table closes cleanly without one.
            () => (
              <ErpGridSummaryFooter
                columns={flattenColumns(mergedColumns).filter(
                  (c) => c.key !== '__erp_actions__'
                )}
                pagedData={pagedData}
                summary={summary}
                selectionColumnWidth={resolvedRowSelection ? 44 : 0}
                hasExpandableColumn={Boolean(expandable)}
              />
            )
          }
          rowClassName={composedRowClassName}
          className={`erp-grid__table ${tableClassName}`}
        />
      )}

      {!showError && !showInitialSkeleton && usePagination ? (
        <ErpDataGridPagination
          current={currentPage}
          pageSize={currentPageSize}
          total={
            serverMode
              ? Number.isFinite(totalProp)
                ? totalProp
                : 0
              : filteredData.length
          }
          pageSizeOptions={pageSizeOptions}
          onChange={handlePaginationChange}
        />
      ) : null}

      {renderForm && onSubmit ? (
        <ErpDataGridFormModal
          open={modal.open}
          mode={modal.mode}
          record={modal.record}
          renderForm={renderForm}
          formInitialValues={formInitialValues}
          formTitles={formTitles}
          width={formModalWidth}
          onSubmit={handleFormSubmit}
          onCancel={closeModal}
        />
      ) : null}

      {/* Advanced-filter modal — composes the GridAdvancedFilterGroup
          that drives client- and server-mode filtering. Modal is
          always mounted but unmounted between opens via `destroyOnClose`
          so we re-seed from the current state on every open. */}
      <ErpGridAdvancedFilterModal
        open={advancedFilterOpen}
        columns={columns}
        initialGroup={advancedFilters}
        onApply={handleAdvancedFilterApply}
        onClose={() => setAdvancedFilterOpen(false)}
      />

      {Array.isArray(detailGroups) && detailGroups.length ? (
        <ErpDataGridDetailDrawer
          open={drawer.open}
          record={drawer.record}
          onClose={closeDrawer}
          width={detailDrawerWidth}
          title={resolveDrawerCopy(detailDrawerTitle, drawer.record) || title}
          subtitle={resolveDrawerCopy(detailDrawerSubtitle, drawer.record)}
          groups={detailGroups}
        />
      ) : null}

      {/* Stage 6A.2G — Row context menu. Rendered as a fixed-position
          1×1 anchor at the click point so AntD's Dropdown attaches its
          menu there. The Dropdown is open-controlled by the
          `contextMenu` state; clicking an item runs the action and
          then clears the state. */}
      {hasContextMenu && contextMenu ? (
        <Dropdown
          open
          trigger={['contextMenu']}
          // Stage 6A.2J — scope so the light theme can style the
          // menu without touching every AntD Dropdown app-wide.
          overlayClassName="erp-grid__contextmenu-overlay"
          onOpenChange={(o) => { if (!o) closeContextMenu(); }}
          menu={{
            items: contextMenuActions
              .filter((a) => {
                if (typeof a.visible === 'function' && !a.visible(contextMenu.record)) return false;
                return true;
              })
              .reduce((acc, a, idx, arr) => {
                acc.push({
                  key: a.key,
                  icon: a.icon,
                  label: a.label,
                  danger: !!a.danger,
                  disabled: typeof a.disabled === 'function'
                    ? a.disabled(contextMenu.record)
                    : !!a.disabled
                });
                // Insert divider before the first danger action if it
                // isn't already separated. Keeps `View / Edit ─ Delete`
                // pattern clean without callers needing to specify it.
                const next = arr[idx + 1];
                if (next && next.danger && !a.danger) {
                  acc.push({ type: 'divider', key: `__div_${a.key}` });
                }
                return acc;
              }, []),
            onClick: ({ key }) => {
              const action = contextMenuActions.find((a) => a.key === key);
              closeContextMenu();
              if (action && typeof action.onClick === 'function') {
                // Defer one tick so the dropdown can unmount before any
                // confirm-modal / approval-MFA modal opens. Without
                // this, AntD's Dropdown sometimes traps focus from the
                // confirm-modal on Chrome.
                setTimeout(() => action.onClick(contextMenu.record), 0);
              }
            }
          }}>
          <div
            className="erp-grid__contextmenu-anchor"
            style={{
              position: 'fixed',
              left: contextMenu.x,
              top:  contextMenu.y,
              width: 1, height: 1,
              pointerEvents: 'none'
            }}
          />
        </Dropdown>
      ) : null}
    </div>
  );
}
